import React, { useId } from 'react';
import { useIntl } from 'react-intl';

import { Button, Dialog, IconButton } from '@bolteu/clay-react';
import { Cross } from '@bolteu/kalep-react-icons';

import {
  EvaluationMonth,
  PerformanceTier,
} from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/models';
import { getMonthName } from '@provider-portal/mp2-common/topPerformer';
import { PerformerStatus } from '@provider-portal/mp2-common/topPerformer/components';

import { $tk } from 'utils/appTranslationKey';

const dialogTk = $tk.widgets.boltStarMonthlyUpdateDialog;

const TIER_LABEL_KEYS: Record<PerformanceTier, string> = {
  poor: $tk.widgets.boltStar.tier.poor,
  needs_improvement: $tk.widgets.boltStar.tier.needsImprovement,
  good: $tk.widgets.boltStar.tier.good,
  top: $tk.widgets.boltStar.tier.top,
};

const TIER_COPY_KEYS: Record<PerformanceTier, { title: string; description: string }> = {
  top: dialogTk.top,
  good: dialogTk.good,
  needs_improvement: dialogTk.needsImprovement,
  poor: dialogTk.poor,
};

type DialogChangeEventDetails = Parameters<
  NonNullable<React.ComponentProps<typeof Dialog.Root>['onOpenChange']>
>[1];

export interface BoltStarMonthlyUpdateDialogProps {
  isOpen: boolean;
  /** Fired only by the close button: the backdrop and Escape deliberately do not dismiss. */
  onClose: () => void;
  onDismiss: () => void;
  onAction: () => void;
  month: EvaluationMonth;
  tier: PerformanceTier;
}

export function BoltStarMonthlyUpdateDialog({
  isOpen,
  onClose,
  onDismiss,
  onAction,
  month,
  tier,
}: BoltStarMonthlyUpdateDialogProps) {
  const intl = useIntl();
  const levelId = useId();
  const descriptionId = useId();
  const copyKeys = TIER_COPY_KEYS[tier];

  const handleOpenChange = (open: boolean, eventDetails: DialogChangeEventDetails) => {
    // block closing via shroud or ESC
    if (!open && eventDetails.reason === 'close-press') {
      onClose();
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} disablePointerDismissal>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup
          className="w-full max-w-110 shadow-lg"
          aria-describedby={`${levelId} ${descriptionId}`}
        >
          <Dialog.Header className="shrink-0 pb-3">
            <Dialog.Close
              render={
                <IconButton
                  aria-label={intl.formatMessage({ id: dialogTk.closeLabel })}
                  icon={<Cross />}
                  size="sm"
                  variant="ghost"
                />
              }
              className="ml-auto"
            />
          </Dialog.Header>
          {/* The whole body scrolls on short viewports instead of squeezing the text, while the
              buttons stay pinned. */}
          <div className="bolt-font-body-m-regular grow overflow-y-auto overscroll-none text-primary">
            <div id={levelId} className="flex flex-col px-6 pb-4">
              <PerformerStatus
                tier={tier}
                caption={intl.formatMessage(
                  { id: dialogTk.levelCaption },
                  { month: getMonthName(month) },
                )}
                tierLabelKeys={TIER_LABEL_KEYS}
                benefitLabelKeys={dialogTk.benefit}
              />
            </div>
            <Dialog.Title
              className="bolt-font-heading-xs-accent m-0 px-6 pb-2 pt-4"
              // eslint-disable-next-line jsx-a11y/heading-has-content -- content added by title component
              render={<h2 />}
            >
              {intl.formatMessage({ id: copyKeys.title })}
            </Dialog.Title>
            <Dialog.Description id={descriptionId} className="m-0 overflow-visible" render={<p />}>
              {intl.formatMessage({ id: copyKeys.description })}
            </Dialog.Description>
          </div>
          <Dialog.Footer className="shrink-0 flex-col gap-2">
            <Button className="w-full" size="md" variant="primary" onClick={onAction}>
              {intl.formatMessage({ id: dialogTk.actionLabel })}
            </Button>
            <Button className="w-full" size="md" variant="tertiary" onClick={onDismiss}>
              {intl.formatMessage({ id: dialogTk.dismissLabel })}
            </Button>
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
